import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { registerPrograms } from "@/lib/checkout";
import { previewAgreements } from "@/lib/legal";
import StepParticipant, { validateParticipant } from "@/components/registration/StepParticipant";
import StepAgreements from "@/components/registration/StepAgreements";
import { calcAge } from "@/components/registration/ui";
import { usd } from "@/lib/training";

const BLANK = {
  first_name: "", last_name: "", dob: "", division: "", guardian_name: "", email: "", phone: "",
  emergency_name: "", emergency_phone: "", med_conditions: "", med_allergies: "", med_medications: "", med_injuries: "", med_other: "",
  accuracy_ack: false, signature: "", media_consent: "",
};
const norm = (s) => (s || "").trim().toLowerCase().split(/\s+/).join(" ");
const STEPS = ["Athlete & parent", "Agreements", "Payment"];

function fromPrefill(p, division) {
  const m = p?.medical || {};
  return {
    ...BLANK, ...(p ? Object.fromEntries(Object.keys(BLANK).filter((k) => p[k] !== undefined && typeof p[k] === "string").map((k) => [k, p[k]])) : {}),
    med_conditions: m.conditions || "", med_allergies: m.allergies || "", med_medications: m.medications || "", med_injuries: m.injuries || "", med_other: m.other || "",
    division: division || p?.division || "",
  };
}

export default function RegistrationFlow({ offering, prefill, reenrollToken, onConflict }) {
  const pid = offering.lesson ? "lesson" : offering.base_id || offering.id;
  const extra = offering.lesson ? { lesson: offering.lesson } : offering.dropin_session_id ? { dropin_session_id: offering.dropin_session_id } : {};
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => fromPrefill(prefill, offering.age_group));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [accept, setAccept] = useState({});
  const ref = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [step]);

  const loadPreview = async () => {
    const data = await previewAgreements({
      ...extra,
      program_ids: [pid], participant_name: `${form.first_name.trim()} ${form.last_name.trim()}`,
      participant_dob: form.dob, guardian_name: form.guardian_name.trim(),
    });
    if ((preview?.agreements || []).map((a) => a.details_hash).join() !== data.agreements.map((a) => a.details_hash).join()) setAccept({});
    setPreview(data);
  };

  const next = async () => {
    let e = {};
    if (step === 1) e = validateParticipant(form);
    if (step === 2) {
      if (!accept.master) e.master = "You must accept the Participant Service Agreement.";
      preview.agreements.forEach((a) => { if (!accept[a.program_id]) e[`ea_${a.program_id}`] = "You must accept this Program Enrollment Agreement."; });
      if (!form.signature.trim()) e.signature = "Your typed signature is required.";
      else if (norm(form.signature) !== norm(form.guardian_name)) e.signature = `Signature must match the guardian name: ${form.guardian_name.trim()}`;
      if (form.media_consent !== "agree" && form.media_consent !== "decline") e.media_consent = "Please choose one photo and video option.";
    }
    setErrors(e);
    if (Object.keys(e).length) return;
    if (step === 1) {
      setBusy(true);
      try { await loadPreview(); } catch (err) { setErrors({ submit: err?.response?.data?.detail || "Could not load program details." }); setBusy(false); return; }
      setBusy(false);
    }
    setStep((s) => s + 1);
  };

  const pay = async () => {
    setBusy(true);
    setErrors({});
    try {
      const { url } = await registerPrograms({
        ...extra,
        program_ids: [pid],
        athlete: { name: `${form.first_name.trim()} ${form.last_name.trim()}`, first_name: form.first_name.trim(), last_name: form.last_name.trim(), dob: form.dob, age: calcAge(form.dob), division: form.division },
        parent: { name: form.guardian_name.trim(), email: form.email, phone: form.phone },
        emergency: { name: form.emergency_name, phone: form.emergency_phone },
        medical: { conditions: form.med_conditions, allergies: form.med_allergies, medications: form.med_medications, injuries: form.med_injuries, other: form.med_other },
        legal: {
          master_accepted: !!accept.master,
          enrollment_accepted: Object.fromEntries(preview.agreements.filter((a) => accept[a.program_id]).map((a) => [a.program_id, a.details_hash])),
          signature: form.signature.trim(), accuracy_ack: form.accuracy_ack,
        },
        media_consent: form.media_consent, leaderboard_consent: !!form.leaderboard_consent, reenroll_token: reenrollToken || "",
      });
      window.location.href = url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && String(d || "").includes("changed")) {
        try { await loadPreview(); } catch (_) { /* shown below */ }
        setAccept({});
        setStep(2);
      } else if (err?.response?.status === 409 && onConflict) {
        onConflict(typeof d === "string" ? d : "");
        return;
      }
      setErrors({ submit: typeof d === "string" ? d : "Something went wrong. Please try again." });
      setBusy(false);
    }
  };

  return (
    <section ref={ref} data-testid="registration-flow" className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-8 scroll-mt-24">
      <div className="flex items-center gap-2 flex-wrap">
        {STEPS.map((s, i) => (
          <span key={s} data-testid={`reg-step-${i + 1}`} className={`text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5 ${step === i + 1 ? "bg-white text-[#0B0F19]" : step > i + 1 ? "text-white/70 border border-white/20" : "text-white/35 border border-white/10"}`}>{i + 1}. {s}</span>
        ))}
      </div>
      {prefill && step === 1 && <p data-testid="reenroll-prefill-note" className="mt-4 rounded-2xl border border-white/15 px-4 py-3 text-xs font-semibold text-white/70">Welcome back — we've pre-filled your details from last cycle. Please review them; you'll accept the agreements again before payment.</p>}
      {step === 1 && <StepParticipant form={form} set={set} errors={errors} />}
      {step === 2 && preview && <StepAgreements master={preview.master} agreements={preview.agreements} form={form} set={set} accept={accept} setAccept={setAccept} errors={errors} />}
      {step === 3 && (
        <ul data-testid="payment-checklist" className="mt-6 space-y-2 text-sm font-medium text-white/65">
          <li className="flex items-center gap-2"><ShieldCheck size={15} className="text-emerald-400" /> Agreements accepted and signed by {form.signature}</li>
          <li className="flex items-center gap-2"><ShieldCheck size={15} className="text-white/40" /> Photo & video: {form.media_consent === "agree" ? "authorized" : "not authorized"}</li>
          <li className="flex items-center gap-2"><ShieldCheck size={15} className="text-white/40" /> One-time charge of {usd(offering.price)} — no recurring charges</li>
        </ul>
      )}
      {errors.submit && <p data-testid="register-error" className="mt-4 text-sm font-semibold text-red-400">{errors.submit}</p>}
      <div className="mt-8 flex items-center justify-between gap-3 border-t border-white/10 pt-6">
        {step > 1 ? (
          <button data-testid="reg-back-btn" onClick={() => { setErrors({}); setStep((s) => s - 1); }} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white"><ArrowLeft size={16} /> Back</button>
        ) : <span />}
        {step < 3 ? (
          <button data-testid="reg-next-btn" onClick={next} disabled={busy} className="inline-flex items-center gap-2 bg-white text-[#0B0F19] hover:bg-white/90 disabled:opacity-50 font-bold px-7 py-3.5 rounded-full transition-colors">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <>Continue <ArrowRight size={18} /></>}
          </button>
        ) : (
          <button data-testid="register-submit-btn" onClick={pay} disabled={busy} className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] disabled:opacity-50 text-white font-bold px-7 py-3.5 rounded-full transition-colors">
            {busy ? <><Loader2 size={18} className="animate-spin" /> Redirecting…</> : <>Pay {usd(offering.price)} Securely <ArrowRight size={18} /></>}
          </button>
        )}
      </div>
      <p className="mt-3 text-center text-xs font-medium text-white/40">Secure payment by Stripe. Your registration is confirmed once payment succeeds.</p>
    </section>
  );
}
